import { useEffect, useRef, useState } from 'react';
import * as checkout from '../services/checkout';
import { ApiError } from '../services/types';

/**
 * The priced basket, kept in step with what the cart is currently showing.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS DEBOUNCED HERE, AND NOT LEFT TO THE CALLER
 * ---------------------------------------------------------------------------
 * `quoteCheckout` is a pure read - see its own note - but "safe to call on
 * every keystroke" is not the same as "cheap to call on every keystroke".
 * `CartScreen` fires this on a basket change, a coupon apply, a coin toggle
 * AND a tip tap, several of which can happen within the same second. Without
 * debouncing, a customer tapping a quantity stepper twice fires two requests
 * that can land out of order, and the slower-but-earlier one can overwrite the
 * newer basket's price with the previous basket's.
 *
 * The shape of the fix is the same as `useRedemption`'s: wait for `input` to
 * sit still, keep the last good quote on screen while a new one is in flight
 * rather than blanking it to a skeleton, and drop any reply that is not from
 * the newest request.
 *
 * ---------------------------------------------------------------------------
 * `null` MEANS "NOTHING TO QUOTE YET", NOT "FREE"
 * ---------------------------------------------------------------------------
 * `input` is `null` before there is enough to ask about - no address resolved
 * yet, say. The hook does not fetch and `quote` stays `null`; the caller reads
 * that as "no price yet", never as a zero total.
 *
 * A FAILED quote is not an error the cart surfaces as a message either - the
 * same request fires again the moment anything about the basket changes, and
 * there is nothing actionable to tell the customer about a single dropped
 * request that a moment's wait does not already fix. But a stale quote must
 * never be *spendable*: see `current` below. The one exception is a service
 * over its per-booking cap, which no wait fixes - see `refusal`.
 */
export type UseCheckoutQuote = {
  /**
   * The last quote that came back successfully. Kept on screen through a
   * transient failure so a flaky retry doesn't blank the price the customer
   * was just looking at - read `current` to know whether it is still good
   * for what's in the basket right now.
   */
  quote: checkout.CheckoutQuoteDto | null;
  /** True only while the FIRST quote for a changed `input` is still coming -
      see `useRedemption` for why this is not raised on every keystroke. */
  loading: boolean;
  /**
   * True only when `quote` is a successful, non-stale price for the
   * CURRENTLY-settled `input`. Goes false the instant `input` changes (before
   * the debounce even starts) and stays false through a failed re-quote -
   * `quote` may still be showing the old number, but it is no longer proven
   * good. Gate "proceed to pay" on this, not on `quote` alone: it is the only
   * way to keep showing a stale price without ever letting checkout run
   * against one.
   */
  current: boolean;
  /**
   * The server's own sentence when it refused the basket for going past a
   * service's per-booking cap, else `null`.
   *
   * The one failure surfaced, because it is not transient: re-asking about
   * the same basket is refused the same way, and without it the cart sat on
   * "Working out your total…" with no way to learn why. The steppers stop at
   * the cap, but a basket saved before a cap was lowered can still be over
   * it - this is how the server's reason reaches the cart.
   */
  refusal: string | null;
  /**
   * Which part of the request `refusal` is about - `items[1].slotStartAt`
   * when one service's own time is the problem, so the cart can say so on
   * that service's row. Null with no refusal, or when the server named none.
   */
  refusalField: string | null;
};

/** How long the input must sit still before it is worth asking about. */
const SETTLE_MS = 400;

/**
 * Refusals whose sentence the cart shows: a basket past a service's monthly
 * cap, a slot past the booking window (a slot chosen yesterday for what is
 * now beyond today plus `advanceBookingDays`), a time that has passed while
 * the cart sat open, and - now that each service may have its own time - a
 * time nobody can be sent for.
 */
const SHOWN_REFUSALS = [
  'SERVICE_QUANTITY_LIMIT',
  'SLOT_BEYOND_BOOKING_WINDOW',
  'SLOT_IN_THE_PAST',
  'SLOT_UNAVAILABLE',
];

const shownDetail = (error: unknown) =>
  error instanceof ApiError
    ? error.errors.find(detail => SHOWN_REFUSALS.includes(detail.code ?? ''))
    : undefined;

const shownRefusal = (error: unknown): string | null =>
  shownDetail(error) ? (error as ApiError).message : null;

export function useCheckoutQuote(
  input: checkout.CheckoutRequest | null,
  /**
   * Bumped to re-ask about a basket that has not itself changed.
   *
   * ---------------------------------------------------------------------------
   * WHY A QUOTE GOES STALE WITHOUT THE BASKET MOVING
   * ---------------------------------------------------------------------------
   * Almost everything on this bill is a platform setting, and ops can change any
   * of it while somebody sits on the cart: the service's price, the flat service
   * fee, how much of a bill coins may pay for, and whether coins may touch the
   * fee at all. The effect below is keyed on `input`, so none of that reaches a
   * screen that is already open - the customer would have had to kill the app.
   *
   * Pull-to-refresh increments this; nothing else does. Same seam, and the same
   * reason, as `useAvailability`'s own `reloadKey`.
   */
  reloadKey?: number,
): UseCheckoutQuote {
  const [quote, setQuote] = useState<checkout.CheckoutQuoteDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [refusalField, setRefusalField] = useState<string | null>(null);

  /* Which request is current - two can be in flight when `input` changes
     twice inside the settle window, and they can land out of order. */
  const attempt = useRef(0);

  useEffect(() => {
    if (!input) {
      setQuote(null);
      setLoading(false);
      setCurrent(false);
      setRefusal(null);
      setRefusalField(null);
      return;
    }

    const mine = ++attempt.current;
    const controller = new AbortController();
    /* Invalidated the moment the basket changes, not only once the settled
       fetch fails - the old quote is unproven for a NEW input the instant it
       exists, whether or not a re-quote ever comes back. */
    setCurrent(false);
    setLoading(true);

    const timer = setTimeout(() => {
      checkout
        .quoteCheckout(input, controller.signal)
        .then(next => {
          if (mine !== attempt.current) {
            return;
          }
          setQuote(next);
          setCurrent(true);
          setRefusal(null);
          setRefusalField(null);
        })
        .catch(error => {
          if (mine !== attempt.current || controller.signal.aborted) {
            return;
          }
          setRefusal(shownRefusal(error));
          setRefusalField(shownDetail(error)?.field ?? null);
          /* See the file note: `quote` is left as-is (still showing the last
             good price) but `current` stays false, so a stale-but-displayed
             quote can be looked at but never paid against. The next change to
             `input` tries again. */
          setCurrent(false);
        })
        .finally(() => {
          if (mine === attempt.current) {
            setLoading(false);
          }
        });
      // Deliberately not JSON-stringified: `input` is expected to be a value
      // the caller only rebuilds (with a new identity) when something it
      // actually cares about has changed - see `CartScreen`'s own `useMemo`.
      // Depending on identity is what makes this settle once renders do,
      // rather than firing on every unrelated re-render of the caller.
    }, SETTLE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    /* `reloadKey` is deliberately in here with `input`: a pull has to reach
       the server even when the basket is identical to the one already quoted. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, reloadKey]);

  return { quote, loading, current, refusal, refusalField };
}

export default useCheckoutQuote;
